/**
 * CalibrationScreen.js
 * =====================
 * 2-target gaze calibration screen (up → down).
 * Collects iris vertical ratio (IVR) samples during each 4-second window.
 * Derives personalised up/down thresholds and stores them in GazeConfigContext.
 *
 * Route: /calibration
 * Navigates to: /home
 *
 * Uses useGazeDetection directly (not useGazeNavigation) so no selections
 * fire while the user is calibrating.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGazeDetection } from './useGazeDetection';
import { useGazeConfig } from './GazeConfigContext';
import './CalibrationScreen.css';

// ─── Configuration ────────────────────────────────────────────────────────────

// Duration (ms) to collect IVR samples while the user holds their gaze on a target.
const COLLECT_MS = 4000;

// Duration (ms) to show a "Get ready…" countdown before data collection starts.
// Must be a whole number of seconds (used for the countdown display).
const READY_MS = 3000;

// Brief pause (ms) between finishing one target and starting the next.
const BETWEEN_MS = 1500;

// If the camera never becomes ready, navigate to /home after this many ms.
const CAMERA_TIMEOUT_MS = 15_000;

const TARGETS = [
  {
    key:      'up',
    label:    'Look UP',
    arrow:    '↑',
    position: 'top',
    hint:     'Exaggerate — look up further than feels natural, past the dot',
  },
];

// ─── Threshold computation ────────────────────────────────────────────────────

/**
 * Given IVR samples collected while the user looked UP, compute the threshold
 * below which we should override to "up". Uses the 65th percentile with a small
 * margin so we don't over-trigger on straight gaze.
 */
function computeUpThreshold(upSamples) {
  if (!upSamples.length) return 0.35;
  const sorted = [...upSamples].sort((a, b) => a - b);
  const idx    = Math.floor(sorted.length * 0.65);
  return Math.min(sorted[idx] + 0.05, 0.42);
}


// ─── Ding sound ──────────────────────────────────────────────────────────────

function playDing() {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type            = 'sine';
    osc.frequency.value = 880; // A5 — light, pleasant tone
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.9);
  } catch (_) {
    // Audio not supported — silently skip
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CalibrationScreen() {
  const navigate              = useNavigate();
  const location              = useLocation();
  // Caller passes returnTo via navigation state so this screen knows where
  // to go after calibration finishes or is skipped.
  const returnTo              = location.state?.returnTo ?? '/landing';
  const { updateCalibration } = useGazeConfig();

  // Phase: 'intro' | 'ready' | 'collecting' | 'done'
  const [phase,          setPhase]          = useState('intro');
  const [targetIndex,    setTargetIndex]    = useState(0);
  const [progress,       setProgress]       = useState(0);   // 0–1 within collect window
  const [readyCountdown, setReadyCountdown] = useState(Math.round(READY_MS / 1000));

  // IVR sample buckets keyed by target.key
  const samplesRef = useRef({ up: [] });

  // Refs so timer/interval callbacks always read the latest values
  const collectingRef = useRef(false);
  const phaseRef      = useRef('intro');

  // ── Gaze detection (no calibration prop — we collect raw IVR here) ───────
  const { videoRef, isReady, irisVerticalRatio } = useGazeDetection({ enabled: true });

  // Keep a ref to the latest IVR so interval callbacks don't get stale values
  const ivrRef = useRef(0.5);
  useEffect(() => { ivrRef.current = irisVerticalRatio; }, [irisVerticalRatio]);

  // ── Navigate helper ───────────────────────────────────────────────────────
  const navigatedRef = useRef(false);
  const goHome = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    navigate(returnTo, { replace: true });
  }, [navigate, returnTo]);

  // ── Camera timeout: if isReady never fires, don't leave user stuck ────────
  useEffect(() => {
    const t = setTimeout(() => {
      if (phaseRef.current === 'intro') {
        console.warn('[Calibration] Camera timeout — navigating with default thresholds.');
        goHome();
      }
    }, CAMERA_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [goHome]);

  // ── Collection loop ───────────────────────────────────────────────────────
  const runTarget = useCallback((index) => {
    if (index >= TARGETS.length) {
      // All targets done — compute threshold and finish
      const { up }      = samplesRef.current;
      const upThreshold = computeUpThreshold(up);

      console.log('[Calibration] Computed upThreshold:', upThreshold);
      updateCalibration({ upThreshold });

      setPhase('done');
      phaseRef.current = 'done';
      playDing();

      setTimeout(goHome, 2000);
      return;
    }

    const target = TARGETS[index];
    setTargetIndex(index);

    // ── Ready phase: show countdown before collecting ─────────────────────
    setPhase('ready');
    phaseRef.current = 'ready';
    collectingRef.current = false;

    const readySteps = Math.round(READY_MS / 1000);
    setReadyCountdown(readySteps);

    // Tick the countdown down once per second during the ready window
    const countdownId = setInterval(() => {
      setReadyCountdown(c => Math.max(0, c - 1));
    }, 1000);

    const readyTimer = setTimeout(() => {
      clearInterval(countdownId);

      // ── Collecting phase ────────────────────────────────────────────────
      setPhase('collecting');
      phaseRef.current = 'collecting';
      collectingRef.current = true;
      setProgress(0);

      const startTime  = Date.now();
      const intervalId = setInterval(() => {
        if (!collectingRef.current) {
          clearInterval(intervalId);
          return;
        }

        const elapsed = Date.now() - startTime;
        setProgress(Math.min(elapsed / COLLECT_MS, 1));
        samplesRef.current[target.key].push(ivrRef.current);

        if (elapsed >= COLLECT_MS) {
          clearInterval(intervalId);
          collectingRef.current = false;
          setProgress(1);
          // Brief pause before the next target so the screen doesn't jump
          setTimeout(() => runTarget(index + 1), BETWEEN_MS);
        }
      }, 50); // sample at 20 Hz

    }, READY_MS);

    return () => {
      clearTimeout(readyTimer);
      clearInterval(countdownId);
    };
  }, [goHome, updateCalibration]);

  // ── Start calibration once MediaPipe is ready ─────────────────────────────
  useEffect(() => {
    if (!isReady || phase !== 'intro') return;
    const t = setTimeout(() => runTarget(0), 2500);
    return () => clearTimeout(t);
  }, [isReady, phase, runTarget]);

  // ── Derived display values ────────────────────────────────────────────────
  const target     = TARGETS[targetIndex] ?? TARGETS[0];
  const secsLeft   = Math.ceil(COLLECT_MS * (1 - progress) / 1000); // countdown during collection

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="calib-root">
      {/* Hidden video element required by useGazeDetection */}
      <video ref={videoRef} style={{ display: 'none' }} playsInline muted />

      {/* Skip button — always visible; uses default thresholds */}
      <button className="calib-skip" onClick={goHome} aria-label="Skip calibration">
        Skip
      </button>

      {/* ── Intro ── */}
      {phase === 'intro' && (
        <div className="calib-intro">
          <h2>Eye Calibration</h2>
          <p>
            A dot will appear at the <strong>top</strong> of the screen.
          </p>
          <p>
            Look toward it and{' '}
            <strong>exaggerate your gaze slightly</strong> — look a little further than
            feels natural, past the dot. This helps the camera learn your upward eye range.
          </p>
          <p>
            Hold your gaze still until the bar fills.{' '}
            <strong>You'll hear a ding</strong> when it's done — then you can look away.
          </p>
          {!isReady
            ? <p className="calib-waiting">Starting camera…</p>
            : <p className="calib-waiting">Get ready…</p>
          }
        </div>
      )}

      {/* ── Ready / Collecting ── */}
      {(phase === 'ready' || phase === 'collecting') && (
        <>
          {/* Target dot */}
          <div className={`calib-target calib-target--${target.position} ${phase === 'collecting' ? 'calib-target--active' : ''}`}>
            <div className="calib-dot" />
          </div>

          {/* Direction arrow — large, centred, hard to miss */}
          <div className="calib-arrow" aria-hidden="true">
            {phase === 'collecting' ? target.arrow : ''}
          </div>

          {/* Instruction text */}
          <div className="calib-label">
            {phase === 'ready'
              ? <><span className="calib-countdown">{readyCountdown}</span><span className="calib-ready-text">Get ready to {target.label.toLowerCase()}</span></>
              : target.label
            }
          </div>

          {/* Exaggeration hint — shown during both ready and collecting */}
          <div className="calib-hint">{target.hint}</div>

          {/* Progress bar + seconds remaining (only while collecting) */}
          {phase === 'collecting' && (
            <div className="calib-progress-area">
              <div className="calib-secs-left">{secsLeft}s</div>
              <div className="calib-progress-track">
                <div
                  className="calib-progress-fill"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Step counter */}
          <div className="calib-step">
            Step {targetIndex + 1} of {TARGETS.length}
          </div>
        </>
      )}

      {/* ── Done ── */}
      {phase === 'done' && (
        <div className="calib-done">
          <div className="calib-done-check">✓</div>
          <p>Calibration complete.</p>
          <p className="calib-done-sub">Starting now…</p>
        </div>
      )}
    </div>
  );
}
